import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../AxiosInstance";
import { isAdmin } from "../../../../../utils/roles";
import { formatTimestamp } from "../../../../../utils/utils";
import styles from "./current_connection_card.module.css";

const MEDIA = ["ethernet", "wifi", "lte"];
const DEFAULT_ORDER = ["ethernet", "wifi", "lte"];
const STALE_SECONDS = 600;
const RELOAD_MS = 20000;

function secondsSince(iso) {
  const ts = new Date(iso).getTime();
  if (!iso || Number.isNaN(ts)) return null;
  return Math.max(0, Math.round((Date.now() - ts) / 1000));
}

function agoText(t, iso) {
  const s = secondsSince(iso);
  if (s === null) return "";
  if (s < 60) return t("netstate.agoSeconds", { n: s });
  if (s < 3600) return t("netstate.agoMinutes", { n: Math.round(s / 60) });
  return t("netstate.agoHours", { n: Math.round(s / 3600) });
}

function stripPrefix(ip) {
  return (ip || "").split("/")[0];
}

/** Medios que el equipo no tiene (lo informa en su conexión actual: sin
 * interfaz WiFi, como un CM4 sin inalámbrico, o sin módem). No se muestran ni
 * se configuran ni entran en la prioridad. Sin datos del equipo no se oculta
 * nada. */
export function absentMedia(state) {
  const media = (state && state.media) || {};
  return MEDIA.filter((m) => media[m] && media[m].present === false);
}

/** El orden de prioridad sin los medios que el equipo no tiene. */
export function visibleOrder(order, absent) {
  return (order || []).filter((m) => !(absent || []).includes(m));
}

/** Estado de un medio en palabras y color: ok | warn | bad | muted. */
function mediumStatus(medium, info) {
  if (!info || !info.present) return ["absent", "muted"];
  const conn = info.connectivity;
  const withInternet = conn === "full" || conn === null || conn === undefined;
  if (info.default) return ["inUse", withInternet ? "ok" : "warn"];
  if (medium === "ethernet" && info.cable === false) return ["noCable", "muted"];
  if (medium === "wifi") {
    if (info.mode === "ap") return ["apMode", "muted"];
    if (info.mode === "off") return ["off", "muted"];
  }
  if (medium === "lte") {
    if (info.sim === "missing") return ["noSim", "bad"];
    if (info.sim === "locked") return ["simLocked", "bad"];
  }
  if (info.state === "connected") return conn === "full" ? ["ok", "ok"] : ["limited", "warn"];
  if (medium === "lte" && lteSearching(info)) return ["searching", "warn"];
  if (info.state && info.state.startsWith("connecting")) return ["connecting", "warn"];
  return ["disconnected", "muted"];
}

const LTE_REGISTERED = ["home", "roaming", "home-csfb-not-preferred", "roaming-csfb-not-preferred"];

/** El módem busca red: sin registro en un operador no hay APN que conecte,
 * aunque NetworkManager figure "conectando" (el LTE automático lo intenta). */
function lteSearching(info) {
  if (info.modem_state === "searching" || info.registration === "searching") return true;
  const connecting = !!(info.state && info.state.startsWith("connecting"));
  return connecting && !!info.registration && !LTE_REGISTERED.includes(info.registration);
}

function mediumDetail(t, medium, info) {
  if (!info || !info.present) return "";
  const parts = [];
  if (medium === "ethernet") {
    if (info.ip) parts.push(stripPrefix(info.ip));
    if (info.speed_mbps) parts.push(`${info.speed_mbps} Mbit/s`);
  } else if (medium === "wifi") {
    if (info.mode === "client") {
      if (info.ssid) parts.push(`«${info.ssid}»`);
      if (info.signal_pct !== null && info.signal_pct !== undefined) parts.push(t("netstate.signal", { pct: info.signal_pct }));
      if (info.ip) parts.push(stripPrefix(info.ip));
    }
  } else if (medium === "lte") {
    if (info.operator) parts.push(info.operator);
    if (info.access_tech) parts.push(info.access_tech.toUpperCase());
    if (info.signal_pct !== null && info.signal_pct !== undefined) parts.push(t("netstate.signal", { pct: info.signal_pct }));
    if (info.ip) parts.push(stripPrefix(info.ip));
    const apn = lteApn(t, info);
    if (apn) parts.push(apn);
  }
  return parts.join(" · ");
}

/** APN del LTE: automático (elegido según la SIM) o configurado a mano. */
function lteApn(t, info) {
  if (!info || (info.apn_mode !== "auto" && info.apn_mode !== "manual")) return "";
  if (info.apn === null || info.apn === undefined) return "";
  if (info.apn_mode === "auto") return info.apn ? t("netstate.apnAuto", { apn: info.apn }) : t("netstate.apnNetworkAuto");
  return info.apn ? t("netstate.apnManual", { apn: info.apn }) : "";
}

function headline(t, state) {
  const medium = state.uplink;
  if (!medium) return t("netstate.noUplink");
  const info = (state.media || {})[medium] || {};
  const name = t(`netstate.medium.${medium}`);
  const detail = mediumDetail(t, medium, info);
  return `${t("netstate.uplinkVia")} ${name}${detail ? ` · ${detail}` : ""}`;
}

/**
 * "Conexión actual": por qué medio sale a internet el equipo, cómo está cada
 * medio, el orden de prioridad (editable por administradores) y los últimos
 * cambios de salida. Lo manda el equipo (topic network_state) al cambiar y
 * cada 5 minutos; NetworkSection pasa lo que llega en vivo por WebSocket.
 */
export default function CurrentConnectionCard({ pdui_id, liveState, livePriorityStatus, onConfigure, onAbsentMedia }) {
  const { t } = useTranslation();
  const admin = isAdmin();
  const [data, setData] = useState(null);
  const [order, setOrder] = useState(null);
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [, setTick] = useState(0);

  const load = useCallback(() => {
    axiosInstance
      .get(`pdui/${pdui_id}/uplink-changes/?limit=5`)
      .then((response) => setData(response.data))
      .catch(() => setMessage({ type: "error", text: t("netstate.error") }));
  }, [pdui_id, t]);

  useEffect(() => {
    load();
    const id = setInterval(load, RELOAD_MS);
    const clock = setInterval(() => setTick((n) => n + 1), 15000);
    return () => {
      clearInterval(id);
      clearInterval(clock);
    };
  }, [load]);

  // Lo que llega en vivo reemplaza al estado guardado.
  useEffect(() => {
    if (!liveState) return;
    setData((prev) => ({ ...(prev || {}), state: liveState, state_at: liveState.received_at || new Date().toISOString() }));
    load();
  }, [liveState, load]);

  useEffect(() => {
    if (!livePriorityStatus) return;
    load();
  }, [livePriorityStatus, load]);

  const priority = (data && data.priority) || {};
  const reported = (priority.reported && priority.reported.length ? priority.reported : null)
    || ((data && data.state && data.state.priority) || null);
  const currentOrder = (priority.requested && priority.requested.length ? priority.requested : null) || reported || DEFAULT_ORDER;

  useEffect(() => {
    if (!edited) setOrder(currentOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentOrder), edited]);

  const state = data && data.state && data.state.media ? data.state : null;
  const absent = absentMedia(state);
  const absentKey = absent.join(",");

  // NetworkSection oculta también las pestañas de configuración de esos medios.
  useEffect(() => {
    if (onAbsentMedia) onAbsentMedia(absentKey ? absentKey.split(",") : []);
  }, [absentKey, onAbsentMedia]);

  // Se ordena solo lo que el equipo tiene; al guardar, la plataforma deja
  // los que faltan al final (network_state.validate_priority).
  const move = (index, delta) => {
    const next = visibleOrder(order || currentOrder, absent);
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
    setEdited(true);
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await axiosInstance.put(`pdui/${pdui_id}/network-priority/`, { order });
      setData((prev) => ({ ...(prev || {}), priority: response.data }));
      setEdited(false);
      setMessage({ type: "success", text: t("netstate.saved") });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || t("netstate.error") });
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await axiosInstance.post(`pdui/${pdui_id}/network-state-refresh/`);
      setMessage({ type: "info", text: t("netstate.refreshing") });
      setTimeout(load, 4000);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || t("netstate.error") });
    } finally {
      setBusy(false);
    }
  };

  const stateAt = state ? (state.received_at || data.state_at) : null;
  const stale = stateAt ? secondsSince(stateAt) > STALE_SECONDS : false;
  const changes = (data && data.changes) || [];
  const status = priority.status || {};
  const shownOrder = visibleOrder(order || currentOrder, absent);
  const wifi = state ? state.media.wifi || {} : {};
  // Lo que confirmó el equipo al cambiar el orden, o lo que informa en cada
  // conexión actual (el chequeo de internet de NetworkManager).
  const checkStatus = status.connectivity_check || (state && state.connectivity_check) || null;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>{t("netstate.title")}</span>
        <button className={styles.smallButton} onClick={refresh} disabled={busy}>
          {t("netstate.refresh")}
        </button>
      </div>

      {message && <div className={`${styles.message} ${styles[message.type]}`}>{message.text}</div>}

      {!state && <div className={styles.empty}>{t("netstate.noData")}</div>}

      {state && (
        <>
          <div className={styles.headline}>
            <span className={`${styles.dot} ${state.internet === false || !state.uplink ? styles.bad : styles.ok}`} />
            <span className={styles.headlineText}>{headline(t, state)}</span>
            {state.internet !== null && state.internet !== undefined && (
              <span className={`${styles.badge} ${state.internet ? styles.badgeOk : styles.badgeBad}`}>
                {state.internet ? t("netstate.internetOk") : t("netstate.internetFail")}
              </span>
            )}
          </div>
          <div className={styles.updated}>
            {t("netstate.updated", { when: agoText(t, stateAt) })}
            {stale ? ` (${t("netstate.stale")})` : ""}
          </div>

          <div className={styles.media}>
            {MEDIA.filter((m) => !absent.includes(m)).map((medium) => {
              const info = state.media[medium];
              const [key, tone] = mediumStatus(medium, info);
              const rank = shownOrder.indexOf(medium) + 1;
              return (
                <div key={medium} className={`${styles.medium} ${info && info.default ? styles.inUse : ""}`}>
                  <span className={styles.rank}>{rank > 0 ? rank : "-"}</span>
                  <span className={styles.mediumName}>{t(`netstate.medium.${medium}`)}</span>
                  <span className={`${styles.chip} ${styles[tone]}`}>{t(`netstate.status.${key}`)}</span>
                  <span className={styles.detail}>{mediumDetail(t, medium, info)}</span>
                  {onConfigure ? (
                    <button type="button" className={styles.configLink} onClick={() => onConfigure(medium)}>
                      {t("netstate.configure")}
                    </button>
                  ) : <span />}
                </div>
              );
            })}
          </div>

          {wifi.present && (
            <div className={styles.ap}>
              {wifi.ap && wifi.ap.active
                ? t("netstate.ap", { ssid: wifi.ap.ssid || "PDUI" })
                : t("netstate.apOff")}
            </div>
          )}
        </>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("netstate.priority")}</div>
        <div className={styles.help}>{t("netstate.priorityHelp")}</div>
        <ol className={styles.order}>
          {shownOrder.map((medium, index) => (
            <li key={medium} className={styles.orderItem}>
              <span className={styles.orderName}>{index + 1}. {t(`netstate.medium.${medium}`)}</span>
              {admin && (
                <span className={styles.orderButtons}>
                  <button className={styles.iconButton} onClick={() => move(index, -1)} disabled={index === 0 || busy}
                          aria-label={t("netstate.up")} title={t("netstate.up")}>↑</button>
                  <button className={styles.iconButton} onClick={() => move(index, 1)}
                          disabled={index === shownOrder.length - 1 || busy}
                          aria-label={t("netstate.down")} title={t("netstate.down")}>↓</button>
                </span>
              )}
            </li>
          ))}
        </ol>
        {admin && edited && (
          <button className={styles.button} onClick={save} disabled={busy}>
            {busy ? t("netstate.saving") : t("netstate.save")}
          </button>
        )}
        <div className={styles.help}>
          {priority.pending ? t("netstate.pending") : status.at ? `${t("netstate.applied")} · ${formatTimestamp(status.at)}` : ""}
          {checkStatus && (
            <span>
              {priority.pending || status.at ? " · " : ""}
              {["enabled", "unchanged"].includes(checkStatus) ? t("netstate.checkOn") : t("netstate.checkOff")}
            </span>
          )}
          {status.error && <span className={styles.errorText}> · {status.error}</span>}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("netstate.changes")}</div>
        {changes.length === 0 && <div className={styles.help}>{t("netstate.noChanges")}</div>}
        {changes.map((c) => (
          <div key={c.at} className={styles.change}>
            <span className={styles.changeAt}>{formatTimestamp(c.at)}</span>
            <span>
              {c.previous ? t(`netstate.medium.${c.previous}`) : t("netstate.none")}
              {" → "}
              {c.current ? t(`netstate.medium.${c.current}`) : t("netstate.none")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
